import { StrictMode, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { ThemeProvider } from './theme/ThemeProvider';
import { GatewayContext } from './api/useGateway';
import { GatewayClient } from './api/gateway';
import { registerStoreDispatcher } from './api/messageDispatcher';
import { useConnectionStore } from './stores/connectionStore';
import { initAuth } from './stores/authStore';
import { useFavouritesStore } from './stores/favouritesStore';
import { router } from './router';
import { AppErrorBoundary } from './layout/AppErrorBoundary';
import { ConfirmProvider } from './components/common/ConfirmProvider';
import { initSentry } from './observability/sentry';
import { registerSW } from 'virtual:pwa-register';
import './app.css';

// Service-worker auto-update. The SW is built with skipWaiting + clientsClaim,
// so a new deploy's SW ACTIVATES immediately — but the already-loaded page keeps
// running the OLD in-memory JS until something reloads it. Without this, a deploy
// shipped but the open tab rendered the prior bundle until a MANUAL hard-refresh
// (the "I ran the rebuild but see no change" trap). `onNeedRefresh` fires when a
// new SW has taken over; we reload ONCE to swap to the fresh chunks. A reload
// guard prevents loops if the controllerchange fires more than once.
if (!import.meta.env.DEV) {
  let reloaded = false;
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // Apply the waiting SW, then reload when it takes control.
      void updateSW(true);
    },
  });
  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}

// Runtime config injected by docker-entrypoint.sh (production) or empty in dev.
// Falls back to import.meta.env for Vite dev server.
const runtimeConfig = (window as unknown as { __LYNX_CONFIG__?: Record<string, string> })
  .__LYNX_CONFIG__ ?? {};

function cfg(key: string, viteKey: string): string | undefined {
  return runtimeConfig[key] || import.meta.env[viteKey] || undefined;
}

// Initialize Sentry for frontend error reporting (noop if DSN not set). The SDK
// is loaded via dynamic import inside initSentry so it stays off the eager
// bundle / critical path — see ./observability/sentry.
initSentry(cfg('SENTRY_DSN', 'VITE_SENTRY_DSN'), import.meta.env.MODE);

// Refresh the access token from the HttpOnly cookie on page load. Once the
// session is settled (authenticated or not), pull the user's favourites from
// the server and merge any anon localStorage entries.
void initAuth().then(() => {
  void useFavouritesStore.getState().syncFromServer();
});

const WS_URL =
  cfg('WS_URL', 'VITE_WS_URL') ??
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

function App() {
  const gatewayRef = useRef<GatewayClient | null>(null);

  if (!gatewayRef.current) {
    gatewayRef.current = new GatewayClient(WS_URL);
  }

  const gateway = gatewayRef.current;

  useEffect(() => {
    gateway.connect();
    const unsubscribe = registerStoreDispatcher(gateway);

    return () => {
      unsubscribe();
      gateway.disconnect();
    };
  }, [gateway]);

  // Subscribe to ticker channel — re-subscribe on reconnect (new clientId)
  const clientId = useConnectionStore((s) => s.clientId);
  useEffect(() => {
    if (!clientId) return;
    const unsubTicker = gateway.subscribe('ticker');
    return () => { unsubTicker(); };
  }, [gateway, clientId]);

  return (
    <GatewayContext.Provider value={gateway}>
      <AppErrorBoundary>
        <ThemeProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </ThemeProvider>
      </AppErrorBoundary>
    </GatewayContext.Provider>
  );
}

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
